// Адрес раздела «Найм». Правило 4: в адресе кодируются раздел, представление, отбор, строка,
// вкладка карточки и открытая панель — ссылку коллега открывает в том же состоянии.
//
//   /hiring/candidates                             воронка кандидатов
//   /hiring/candidates?f.status=new                 этап воронки
//   /hiring/candidates?f.owner=<id>&q=иванов        ответственный плюс поиск
//   /hiring/candidates/r/<id>?tab=applications      карточка кандидата, вкладка «Отклики»
//   /hiring/funnel?f.since=2026-08-01&f.stuck=14    воронка: этапы, конверсия, зависание
//   /hiring/blacklist?f.status=active&q=старцев     чёрный список
//   /hiring/blacklist/r/<id>                        карточка записи
//   /hiring/drafts?f.import=<id>&f.verdict=pending  предложения из выгрузки мессенджера
//   /hiring/checks?f.candidate=<id>                 журнал сверок
//   /hiring/accounts/r/<id>?tab=vacancies           учётная запись площадки, её вакансии
//   /hiring/dialogs?f.status=waiting_operator       переписки, ждущие человека
//   /hiring/dialogs/r/<id>                          лента одного диалога
//   /hiring/library?f.status=draft                  формулировки помощника
//   ?p=new-candidate:new                            мастер поверх любого представления
//
// Восемь представлений — это не восемь экранов. Скелет один (правило 1), а представление
// заводится там, где МЕНЯЕТСЯ СМЫСЛ СТРОКИ (правило 2): кандидат, запись чёрного списка,
// предложение из выгрузки, событие сверки, учётная запись площадки, переписка, формулировка.
// Свести их отбором внутри одного списка нельзя — у строк разная грамматика, и подпись над
// списком («74 кандидата») перестала бы что-либо означать.
//
// Чистые функции без React: их гоняют тесты и переиспользуют все экраны раздела.

import { buildPath } from '@/shared/routing/paths';
import { buildSearch, pushPanel, type SearchState } from '@/shared/routing/search';

export const SECTION = 'hiring';

/**
 * Слаги представлений. Первые четыре объявлены в реестре разделов (`app/sections.ts`) как
 * стартовые по правам — их менять нельзя, это контракт с каркасом.
 */
export const HIRING_VIEWS = [
  'candidates',
  'funnel',
  'blacklist',
  'drafts',
  'checks',
  'accounts',
  'dialogs',
  'library',
] as const;

export type HiringView = (typeof HIRING_VIEWS)[number];

/** Воронка кандидатов: главное представление раздела, здесь работает рекрутёр. */
export const DEFAULT_VIEW: HiringView = 'candidates';

export function isHiringView(value: string | undefined): value is HiringView {
  return value !== undefined && (HIRING_VIEWS as readonly string[]).includes(value);
}

/**
 * Право, по которому представление вообще показывается.
 *
 * Раздача разная и задана сервером (`server/app/hiring/api/*.py`, `need(...)`), а не выдумана
 * здесь: финотдел видит только чёрный список, администратор — площадку и помощника, рекрутёр —
 * всё. Список представлений в переключателе собирается ровно по этой таблице, поэтому человек
 * не встречает вкладок, за которыми его ждёт отказ.
 */
export const VIEW_PERMISSION: Readonly<Record<HiringView, string>> = {
  candidates: 'hiring.candidate.view',
  // Воронка, предложения и сверки — РАБОЧИЕ срезы, и права у них свои. Заказчик 10.09.2026
  // попросил оставить смотрящим ровно два экрана: кандидаты и чёрный список. Пока срезы
  // висели на тех же правах, что и главные экраны, «две колонки» не выражались вовсе.
  funnel: 'hiring.candidate.funnel',
  blacklist: 'hiring.blacklist.view',
  drafts: 'hiring.blacklist.review',
  checks: 'hiring.blacklist.review',
  accounts: 'hiring.avito.view',
  dialogs: 'hiring.assistant.view',
  library: 'hiring.assistant.view',
};

/** Названия в переключателе представлений. */
export const VIEW_TITLES: Readonly<Record<HiringView, string>> = {
  candidates: 'Кандидаты',
  funnel: 'Воронка',
  blacklist: 'Чёрный список',
  drafts: 'Предложения',
  checks: 'Сверки',
  accounts: 'Площадка',
  dialogs: 'Переписка',
  library: 'Ответы помощника',
};

// ── Ключи отбора ──────────────────────────────────────────────────────────────────────────
// Голая строка в разметке разъезжается с разбором адреса, поэтому ключи здесь константами.

/** Этап воронки кандидата либо состояние записи, диалога, формулировки — по представлению. */
export const FILTER_STATUS = 'status';
/** Ответственный рекрутёр. */
export const FILTER_OWNER = 'owner';
export const FILTER_CITY = 'city';
export const FILTER_REGION = 'region';
export const FILTER_TAG = 'tag';
/** Учётная запись площадки: разрез, который нужен во всех представлениях найма. */
export const FILTER_ACCOUNT = 'account';
/** Выгрузка мессенджера, из которой пришли предложения. */
export const FILTER_IMPORT = 'import';
/** Решение по предложению: pending, to_blacklist, note_only, discarded. */
export const FILTER_VERDICT = 'verdict';
/** Кандидат, по которому смотрят журнал сверок. */
export const FILTER_CANDIDATE = 'candidate';
/** Шаг разговора в библиотеке формулировок. */
export const FILTER_STEP = 'step';
/** С какой даты считать воронку. */
export const FILTER_SINCE = 'since';
/** Порог зависания на этапе, дней. */
export const FILTER_STUCK = 'stuck';

// ── Вкладки карточек ──────────────────────────────────────────────────────────────────────
// Потолок — восемь на карточку (правило 12). Здесь четыре и три.

export const CANDIDATE_TABS = ['overview', 'applications', 'history', 'hiring'] as const;
export type CandidateTab = (typeof CANDIDATE_TABS)[number];
export const DEFAULT_CANDIDATE_TAB: CandidateTab = 'overview';

export function candidateTab(value: string | undefined): CandidateTab {
  return value !== undefined && (CANDIDATE_TABS as readonly string[]).includes(value)
    ? (value as CandidateTab)
    : DEFAULT_CANDIDATE_TAB;
}

export const ACCOUNT_TABS = ['access', 'vacancies', 'exchange'] as const;
export type AccountTab = (typeof ACCOUNT_TABS)[number];
export const DEFAULT_ACCOUNT_TAB: AccountTab = 'access';

export function accountTab(value: string | undefined): AccountTab {
  return value !== undefined && (ACCOUNT_TABS as readonly string[]).includes(value)
    ? (value as AccountTab)
    : DEFAULT_ACCOUNT_TAB;
}

// ── Панели-мастера ────────────────────────────────────────────────────────────────────────
//
// Все они панели, а не модалки: заведение кандидата, записи, заметки, вакансии и доступов
// обратимо (правило 4). Модалка остаётся за необратимым — за уничтожением записи чёрного
// списка, переводом кандидата в сотрудники и склейкой двух карточек в одну.

export const PANEL_NEW_CANDIDATE = 'new-candidate';
export const PANEL_CANDIDATE_STATUS = 'cand-status';
export const PANEL_CANDIDATE_NOTE = 'cand-note';
export const PANEL_CANDIDATE_TAGS = 'cand-tags';
export const PANEL_NEW_ENTRY = 'new-entry';
export const PANEL_ENTRY_LIFT = 'entry-lift';
export const PANEL_DRAFT_DECISION = 'draft-decision';
export const PANEL_NEW_IMPORT = 'new-import';
export const PANEL_NEW_ACCOUNT = 'new-account';
export const PANEL_CREDENTIALS = 'acc-credentials';
export const PANEL_NEW_VACANCY = 'new-vacancy';
/** Изменить у всех: правка многих размещённых вакансий записи одним действием. */
export const PANEL_BULK_EDIT = 'bulk-edit';
export const PANEL_ASSISTANT_SETTINGS = 'assistant-mode';
export const PANEL_NEW_PHRASE = 'new-phrase';

/** Идентификатор мастера, которому не с чего начинать: заведение «с нуля». */
export const BLANK = 'new';

/** Разобранный адрес раздела: представление, выбранная строка, состояние поисковой строки. */
export interface HiringRoute {
  view: HiringView;
  rowId: string | undefined;
  search: SearchState;
}

export function hiringHref(view: HiringView, search: SearchState, rowId?: string): string {
  return `${buildPath({ section: SECTION, view, rowId })}${buildSearch(search)}`;
}

/** Открыть панель поверх текущего экрана, ничего в нём не меняя. */
export function openPanelHref(route: HiringRoute, panel: string, id: string): string {
  return hiringHref(route.view, pushPanel(route.search, { view: panel, id }), route.rowId);
}

/** Ставит или снимает один отбор, не трогая остальные. */
export function setFilter(
  search: SearchState,
  key: string,
  value: string | undefined,
): SearchState {
  const filters: Record<string, string> = { ...search.filters };
  if (value === undefined || value === '') {
    delete filters[key];
  } else {
    filters[key] = value;
  }
  return { ...search, filters };
}

/**
 * Отборы, осмысленные в каждом представлении. Всё, чего в списке нет, при переключении
 * отбрасывается: «этап воронки» в чёрном списке и «выгрузка» в переписке ничего не значат, а
 * невидимый отбор, уехавший вместе с человеком, даёт пустой список без объяснимой причины.
 */
const FILTERS_BY_VIEW: Readonly<Record<HiringView, readonly string[]>> = {
  candidates: [FILTER_STATUS, FILTER_OWNER, FILTER_CITY, FILTER_TAG],
  funnel: [FILTER_SINCE, FILTER_STUCK],
  blacklist: [FILTER_STATUS, FILTER_REGION],
  drafts: [FILTER_IMPORT, FILTER_VERDICT],
  checks: [FILTER_CANDIDATE],
  accounts: [],
  dialogs: [FILTER_STATUS, FILTER_ACCOUNT],
  library: [FILTER_STATUS, FILTER_STEP, FILTER_ACCOUNT],
};

/**
 * Что переносится при смене представления.
 *
 * Поиск переносится только между теми представлениями, где он вообще есть, — кандидаты и
 * чёрный список: там сервер ищет по фамилии и телефону. Открытая строка и вкладка уходят
 * всегда: они принадлежали прежнему списку, и в новом их нет.
 */
export function switchViewHref(to: HiringView, search: SearchState): string {
  const allowed = FILTERS_BY_VIEW[to];
  const filters: Record<string, string> = {};
  for (const key of allowed) {
    const value = search.filters[key];
    if (value !== undefined) {
      filters[key] = value;
    }
  }
  const searchable = to === 'candidates' || to === 'blacklist';
  return hiringHref(to, {
    ...search,
    q: searchable ? search.q : undefined,
    tab: undefined,
    filters,
    panels: [],
  });
}
